import { useState, useRef, useEffect, useCallback } from 'react';
import { Ball, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, ShapeType, SimulationConfig, AppSave, SpaceSpiralRoundConfig, UserSound, CustomMelody, BallPiece, RhythmicSimulationState } from '@/types';
import { DEFAULT_CONFIG, STORAGE_KEY, GLOBAL_USER_SOUNDS_KEY, GLOBAL_CUSTOM_MELODIES_KEY } from '@/constants/constants';
import { PhysicsSystem } from '@/logic/physics/PhysicsSystem';
import { BallPool } from '@/services/BallPool';
import { TrailCirclePool } from '@/services/TrailCirclePool';
import { CollisionEventPool } from '@/services/CollisionEventPool';
import { BlastEventPool } from '@/services/BlastEventPool';
import { safeGetItem } from '@/lib/storage';
import { persistentStore } from '@/lib/LocalPersistentStore';

export const useSimulationState = () => {
  const [config, _setConfig] = useState<SimulationConfig>(() => {
    // 1. Initial synchronous load from localStorage (best effort)
    const saved = safeGetItem(STORAGE_KEY);
    const globalSounds = JSON.parse(safeGetItem(GLOBAL_USER_SOUNDS_KEY) || '[]');
    const globalMelodies = JSON.parse(safeGetItem(GLOBAL_CUSTOM_MELODIES_KEY) || '[]');
    
    const baseConfig = saved ? { ...DEFAULT_CONFIG, ...JSON.parse(saved) } : DEFAULT_CONFIG;
    
    // Ensure properties are initialized if missing from saved config
    if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.showHUD === undefined) baseConfig.showHUD = false;
        if (parsed.hudShowElapsedTime === undefined) baseConfig.hudShowElapsedTime = false;
        if (parsed.hudShowSpeed === undefined) baseConfig.hudShowSpeed = false;
        if (parsed.showBallSizeCounter === undefined) baseConfig.showBallSizeCounter = false;
        if (parsed.showBounceCounter === undefined) baseConfig.showBounceCounter = false;
        if (parsed.hudPosition === undefined) baseConfig.hudPosition = 'TOP_RIGHT';
        if (parsed.hudOpacity === undefined) baseConfig.hudOpacity = 0.8;
        if (parsed.hudScale === undefined) baseConfig.hudScale = 1.0;
        if (parsed.showControlIsland === undefined) baseConfig.showControlIsland = true;
        
        // Initialize new Title/Footer settings
        if (parsed.titleText === undefined) baseConfig.titleText = "";
        if (parsed.titleFontFamily === undefined) baseConfig.titleFontFamily = "Inter";
        if (parsed.titleFontWeight === undefined) baseConfig.titleFontWeight = "900";
        if (parsed.footerText === undefined) baseConfig.footerText = "";
        if (parsed.footerFontFamily === undefined) baseConfig.footerFontFamily = "Inter";
        if (parsed.footerFontWeight === undefined) baseConfig.footerFontWeight = "500";
    }
    
    // Merge global sounds/melodies
    const mergedSounds = [...(baseConfig.userSounds || [])];
    globalSounds.forEach((gs: UserSound) => {
        if (!mergedSounds.find(s => s.id === gs.id)) {
            mergedSounds.push(gs);
        }
    });

    const mergedMelodies = [...(baseConfig.customMelodies || [])];
    globalMelodies.forEach((gm: CustomMelody) => {
        if (!mergedMelodies.find(m => m.id === gm.id)) {
            mergedMelodies.push(gm);
        }
    });

    return { ...baseConfig, userSounds: mergedSounds, customMelodies: mergedMelodies };
  });

  // Phase 6: Async migration and deep loading from IndexedDB
  useEffect(() => {
    const loadFromIndexedDB = async () => {
        // 1. Check/Migrate Main Config
        let fullConfig = await persistentStore.getJson<SimulationConfig>('SAVES', 'current_config');
        if (!fullConfig) {
            fullConfig = await persistentStore.migrateFromLocalStorage<SimulationConfig>('SAVES', STORAGE_KEY, 'current_config');
        }

        // 2. Check/Migrate Global Stores
        let globalSounds = await persistentStore.getJson<UserSound[]>('AUDIO', 'global_sounds');
        if (!globalSounds) {
            globalSounds = await persistentStore.migrateFromLocalStorage<UserSound[]>('AUDIO', GLOBAL_USER_SOUNDS_KEY, 'global_sounds');
        }

        let globalMelodies = await persistentStore.getJson<CustomMelody[]>('MELODIES', 'global_melodies');
        if (!globalMelodies) {
            globalMelodies = await persistentStore.migrateFromLocalStorage<CustomMelody[]>('MELODIES', GLOBAL_CUSTOM_MELODIES_KEY, 'global_melodies');
        }

        if (fullConfig || globalSounds || globalMelodies) {
            _setConfig(prev => {
                const nextBase = fullConfig ? { ...prev, ...fullConfig } : prev;
                const nextSounds = globalSounds || [];
                const nextMelodies = globalMelodies || [];

                // Merge
                const mergedSounds = [...(nextBase.userSounds || [])];
                nextSounds.forEach((gs: UserSound) => {
                    if (!mergedSounds.find(s => s.id === gs.id)) {
                        mergedSounds.push(gs);
                    }
                });

                const mergedMelodies = [...(nextBase.customMelodies || [])];
                nextMelodies.forEach((gm: CustomMelody) => {
                    if (!mergedMelodies.find(m => m.id === gm.id)) {
                        mergedMelodies.push(gm);
                    }
                });

                return { ...nextBase, userSounds: mergedSounds, customMelodies: mergedMelodies };
            });
        }
    };

    loadFromIndexedDB();
  }, []);

  // Custom setConfig that preserves global sounds/melodies
  const setConfig = useCallback((value: React.SetStateAction<SimulationConfig>) => {
    _setConfig((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        return next;
    });
  }, []);

  const ballCountRef = useRef(0);
  const setBallCount = useCallback((value: React.SetStateAction<number>) => {
    if (typeof value === 'function') {
      ballCountRef.current = value(ballCountRef.current);
    } else {
      ballCountRef.current = value;
    }
  }, []);

  const bounceCountRef = useRef(0);
  const setBounceCount = useCallback((value: React.SetStateAction<number>) => {
    if (typeof value === 'function') {
      bounceCountRef.current = value(bounceCountRef.current);
    } else {
      bounceCountRef.current = value;
    }
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showUI, setShowUI] = useState(true);

  const elapsedTimeRef = useRef(0);
  const setElapsedTime = useCallback((value: React.SetStateAction<number>) => {
    if (typeof value === 'function') {
      elapsedTimeRef.current = value(elapsedTimeRef.current);
    } else {
      elapsedTimeRef.current = value;
    }
  }, []);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUploadingBallSound, setIsUploadingBallSound] = useState(false);
  const [cloudSaves, setCloudSaves] = useState<AppSave[]>([]);
  
  // Auto-save to IndexedDB whenever config changes
  useEffect(() => {
    const saveToIndexedDB = async () => {
        // Save main config
        await persistentStore.setJson('SAVES', 'current_config', config);
        
        // Update global stores
        let globalSounds = await persistentStore.getJson<UserSound[]>('AUDIO', 'global_sounds') || [];
        let globalMelodies = await persistentStore.getJson<CustomMelody[]>('MELODIES', 'global_melodies') || [];
        
        let globalSoundsChanged = false;
        config.userSounds.forEach(s => {
            if (!globalSounds.find((gs: UserSound) => gs.id === s.id)) {
                globalSounds.push(s);
                globalSoundsChanged = true;
            }
        });

        let globalMelodiesChanged = false;
        config.customMelodies.forEach(m => {
            if (!globalMelodies.find((gm: CustomMelody) => gm.id === m.id)) {
                globalMelodies.push(m);
                globalMelodiesChanged = true;
            }
        });

        if (globalSoundsChanged) {
            await persistentStore.setJson('AUDIO', 'global_sounds', globalSounds);
        }
        if (globalMelodiesChanged) {
            await persistentStore.setJson('MELODIES', 'global_melodies', globalMelodies);
        }
    };

    saveToIndexedDB();
  }, [config]);

  const wasPlayingBeforeDarkModeRef = useRef<boolean>(false);
  const isTransitioningRef = useRef<boolean>(false);
  const isAutoPausingRef = useRef<boolean>(false);

  useEffect(() => {
    if (isTransitioningRef.current === false) {
      isTransitioningRef.current = true;
      return;
    }

    if (isPlaying) {
      wasPlayingBeforeDarkModeRef.current = true;
      isAutoPausingRef.current = true;
      setIsPlaying(false);
      
      const timer = setTimeout(() => {
        if (wasPlayingBeforeDarkModeRef.current) {
          setIsPlaying(true);
        }
        wasPlayingBeforeDarkModeRef.current = false;
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    } else {
      wasPlayingBeforeDarkModeRef.current = false;
    }
  }, [config.darkMode]);

  useEffect(() => {
    if (!isPlaying) {
      if (isAutoPausingRef.current) {
        isAutoPausingRef.current = false;
      } else {
        wasPlayingBeforeDarkModeRef.current = false;
      }
    }
  }, [isPlaying]);
  
  const ballsRef = useRef<Ball[]>([]);
  const trailCirclesRef = useRef<TrailCircle[]>([]);
  const collisionEventsRef = useRef<CollisionEvent[]>([]);
  const blastEventsRef = useRef<BlastEvent[]>([]);
  const persistentSpectrumBoostRef = useRef<Float32Array>(new Float32Array(256).fill(0));
  const persistentSpectrumTargetRef = useRef<Float32Array>(new Float32Array(256).fill(0));
  const rhythmicBreakPiecesRef = useRef<BallPiece[]>([]);
  const escapeRingsRef = useRef<EscapeRing[]>([]);
  const initialEscapeRingCountRef = useRef<number>(0); 
  const requestRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(Date.now());
  const hueRef = useRef<number>(0);
  const timeScaleRef = useRef<number>(1.0);
  const isRotationStoppedRef = useRef<boolean>(false);
  const shakeRef = useRef<number>(0);
  const counterPopRef = useRef<number>(1.0);
  const timeRemainingRef = useRef<number>(10);
  
  // Space Spiral Round System
  const currentRoundRef = useRef<number>(0);
  const roundStartTimeRef = useRef<number>(0);
  const roundOverridesRef = useRef<SpaceSpiralRoundConfig | null>(null);
  
  const morphTimerRef = useRef<number>(0); 
  const morphStateRef = useRef({
      active: false,
      from: 'CIRCLE' as ShapeType,
      to: 'CIRCLE' as ShapeType,
      progress: 0
  });
  
  const escapeAccelerationBoostRef = useRef<number>(0);
  const containerRadiusRef = useRef<number>(DEFAULT_CONFIG.containerRadius);
  const speedIncreaseRateRef = useRef<number>(DEFAULT_CONFIG.speedIncreaseRate);
  const ballGrowthRateRef = useRef<number>(DEFAULT_CONFIG.ballGrowthRate);
  const containerShrinkRateRef = useRef<number>(DEFAULT_CONFIG.containerShrinkRate);
  const rhythmicIndexRef = useRef<number>(0); 
  const rhythmicTimeScaleRef = useRef<number>(DEFAULT_CONFIG.rhythmicTimeScale);
  const rhythmicTimeScaleRateRef = useRef<number>(DEFAULT_CONFIG.rhythmicTimeScaleRate);
  const rhythmicResetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rhythmicSimulationsStateRef = useRef<RhythmicSimulationState[]>([]);
  const simTimeRef = useRef<number>(0);

  const rhythmicEscapeShieldActiveRef = useRef<boolean>(false);
  const rhythmicEscapeShieldBouncesRef = useRef<number>(0);
  const rhythmicEscapeShieldTimeRef = useRef<number>(0);
  const rhythmicEscapeShieldAngleRef = useRef<number>(0);

  const physicsSystemRef = useRef<PhysicsSystem>(new PhysicsSystem()); 
  const workerRef = useRef<Worker | null>(null);
  const ballPoolRef = useRef<BallPool>(new BallPool());
  const trailCirclePoolRef = useRef<TrailCirclePool>(new TrailCirclePool());
  const collisionEventPoolRef = useRef<CollisionEventPool>(new CollisionEventPool());
  const blastEventPoolRef = useRef<BlastEventPool>(new BlastEventPool());

  return {
    config, setConfig,
    ballCount: ballCountRef.current, setBallCount,
    ballCountRef,
    bounceCount: bounceCountRef.current, setBounceCount,
    bounceCountRef,
    isPlaying, setIsPlaying,
    showUI, setShowUI,
    elapsedTime: elapsedTimeRef.current, setElapsedTime,
    elapsedTimeRef,
    saveStatus, setSaveStatus,
    isAnalyzing, setIsAnalyzing,
    analysisProgress, setAnalysisProgress,
    isUploadingBallSound, setIsUploadingBallSound,
    cloudSaves, setCloudSaves,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicBreakPiecesRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    requestRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef,
    collisionEventPoolRef,
    blastEventPoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef
  };
};
